<template>
    <div
        data-cy="attachment-file-upload"
        class="pointer-events-auto"
        @dragenter.prevent="handleDragEnter"
        @dragleave.prevent="handleDragLeave"
        @dragover.prevent
        @drop.prevent="onDrop"
    >
        <input
            :id="inputId"
            ref="fileInput"
            type="file"
            class="hidden"
            multiple
            @change="uploadFilesWithFilePicker"
        >

        <label :for="inputId" :class="containerClasses">
            <span ref="uploadActionContainer" :class="actionWrapperClasses">
                <UButton
                    size="sm"
                    variant="outline"
                    color="neutral"
                    type="submit"
                    :class="{ 'w-full @[250px]:w-fit': props.shortDesc && props.variant !== 'grid' }"
                    :loading="isLoading"
                    @click="openFileInput"
                >
                    Last opp filer
                </UButton>

                <span v-if="!hideDesc" :class="gridDescClasses">
                    {{
                        shortDesc
                            ? $t('create-transaction.file-uploader-short-desc')
                            : $t('create-transaction.file-uploader-desc')
                    }}
                </span>
            </span>

            <span
                v-if="variant === 'grid' && modelValue.length > 0"
                ref="filesContainer"
                class="gap-1.5 col-start-3 col-span-full grid text-sm max-h-24 overflow-y-auto"
                :class="`grid-cols-${Math.min(modelValue.length, fileItemsCol)}`"
                data-cy="files-container"
            >
                <FileItem
                    v-for="(document, index) in modelValue"
                    :key="document.uuid"
                    :document="document"
                    :is-active="document.uuid === selectedDocumentMetaUuid"
                    :has-url="transactionManagerDocumentUrls.has(document.uuid)"
                    @click="onClickDocumentLink(document.uuid)"
                    @hover="onHoverDocumentLink(document.uuid)"
                    @remove="removeFileAtIndex(index)"
                />
            </span>
        </label>
    </div>
</template>

<script setup lang="ts">
import {transactionManagerDocumentUrls, fileUploadBatch} from '@/services/document.service'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {ATTACHMENT_UPLOADED, ATTACHMENT_UPLOADING} from '@/types/events'
import {computed, inject, onMounted, onUnmounted, Ref, ref} from 'vue'
import {useDocumentPreviewModalService} from '@/transaction-journal/DocumentPreview.modal.service'
import {ToastType, usePanelToast} from '@tritt/multipanel'
import FileItem from '@/document/FileItem.vue'

const {onEvent, cleanUp} = useEmitter()

const {showPanelToast} = usePanelToast()
const {onClickDocumentLink, onHoverDocumentLink, selectedDocumentMetaUuid} = useDocumentPreviewModalService()

const emit = defineEmits(['update:modelValue', 'uploadSuccess'])
const fileDropperHighlighted = ref(false)
// to avoid flickers
let dragCounter = 0

const fileInput = ref<HTMLInputElement | null>(null)
const filesContainer = ref<HTMLSpanElement | null>(null)
const uploadActionContainer = ref<HTMLSpanElement | null>(null)
const panelId: Ref<number> | undefined = inject('panelId')

const inputId = computed(() => `attachment-file-upload${panelId?.value}`)

const isLoading = ref(false)

const containerClasses = computed(() => [
    'cursor-pointer rounded border-2 border-dashed transition-colors',
    fileDropperHighlighted.value
        ? 'border-gray-500 dark:border-gray-500'
        : 'border-gray-400/80 dark:border-gray-600 hover:border-gray-500 dark:hover:border-gray-500',
    props.variant === 'grid' ? 'p-2 grid grid-cols-7 place-items-center' : 'flex',
    !props.hideDesc && props.variant !== 'grid' ? 'p-2' : 'p-0',
    props.shortDesc && props.variant !== 'grid' ? '@container flex items-baseline justify-between w-full flex-col @[250px]:flex-row' : ''
])

const actionWrapperClasses = computed(() => {
    if (props.variant === 'grid') {
        return [
            'flex items-center gap-x-2',
            props.modelValue.length > 0 ? 'col-span-2' : 'place-items-center col-span-full'
        ]
    }

    return [
        'flex items-baseline justify-between w-full gap-2',
        'flex-col @[250px]:flex-row'
    ]
})

const gridDescClasses = computed(() => {
    return props.variant !== 'grid' ? 'text-sm' : ''
})

const props = defineProps({
    modelValue: {
        type: Array as () => DocumentMeta[],
        required: false,
        default: () => []
    },
    variant: {type: String as () => 'grid' | 'simple', default: 'simple'},
    hideDesc: {
        type: Boolean,
        default: false
    },
    fileItemsCol: {
        type: Number,
        default: 5
    },
    shortDesc: {
        type: Boolean,
        default: false
    }
})

const openFileInput = () => {
    fileInput.value?.click()
}

const handleDragEnter = () => {
    dragCounter++
    fileDropperHighlighted.value = true
}

const handleDragLeave = () => {
    dragCounter--
    if (dragCounter <= 0) {
        fileDropperHighlighted.value = false
        dragCounter = 0
    }
}

const removeFileAtIndex = (index: number) => {
    const updatedValue = props.modelValue.filter((_, i) => i !== index)
    emit('update:modelValue', updatedValue)
}

const processUpload = async (files: FileList) => {
    if (files.length === 0) return

    emitter.emit(ATTACHMENT_UPLOADING)

    try {
        const newDocs = await fileUploadBatch(files)
        emit('uploadSuccess', newDocs)
        emit('update:modelValue', [...props.modelValue, ...newDocs])
        emitter.emit(ATTACHMENT_UPLOADED)
    } catch (error) {
        showPanelToast('Kunne ikke laste opp filer', undefined, ToastType.ERROR)
    } finally {
        emitter.emit(ATTACHMENT_UPLOADED)
    }
}

const onDrop = async (e: DragEvent) => {
    dragCounter = 0
    fileDropperHighlighted.value = false

    if (!e.dataTransfer?.files) return

    await processUpload(e.dataTransfer.files)
}

const uploadFilesWithFilePicker = (e: Event) => {
    const target = e.target as HTMLInputElement
    if (target.files) processUpload(target.files)
    target.value = ''
}

defineExpose({open: openFileInput})

onMounted(() => {
    isLoading.value = true
    onEvent(ATTACHMENT_UPLOADING, () => isLoading.value = true)
    onEvent(ATTACHMENT_UPLOADED, () => isLoading.value = false)
    isLoading.value = false
})

onUnmounted(cleanUp)

</script>

<style scoped>

</style>